import { TimeUnit } from "./queryableTypes"

/**
 * The units a client can build an axis for. Narrower than `TimeUnit` because
 * `quarter`/`year` have no local-boundary implementation here yet.
 */
export type BucketUnit = Extract<TimeUnit, "hour" | "day" | "week" | "month">

/**
 * Local-time boundary for a bucket, matching what `DATE_TRUNC(unit, col AT TIME
 * ZONE tz)` produces server-side. The two must agree: `padToAxis` joins server
 * buckets against this axis on exact equality.
 */
export const alignToBucket = (date: Date, unit: BucketUnit) => {
    const aligned = new Date(date)
    if (unit === "hour") {
        aligned.setMinutes(0, 0, 0)
        return aligned
    }
    aligned.setHours(0, 0, 0, 0)
    if (unit === "month") aligned.setDate(1)
    if (unit === "week") {
        const day = aligned.getDay()
        aligned.setDate(aligned.getDate() - day + (day === 0 ? -6 : 1))
    }
    return aligned
}

const advanceOneBucket = (current: Date, unit: BucketUnit) => {
    if (unit === "hour") current.setHours(current.getHours() + 1)
    else if (unit === "day") current.setDate(current.getDate() + 1)
    else if (unit === "week") current.setDate(current.getDate() + 7)
    else current.setMonth(current.getMonth() + 1)
}

export const buildBucketAxis = (start: Date, end: Date, unit: BucketUnit) => {
    const axis: number[] = []
    const current = alignToBucket(start, unit)

    while (current <= end) {
        axis.push(current.getTime())
        advanceOneBucket(current, unit)
    }

    return axis
}

export const padToAxis = (
    axis: number[],
    rows: { bucket: Date | null; value: number }[],
    unit: BucketUnit
) => {
    const byBucket = new Map<number, number>()
    rows.forEach((row) => {
        if (!row.bucket) return
        byBucket.set(alignToBucket(row.bucket, unit).getTime(), row.value)
    })

    return axis.map((x) => ({ x, y: byBucket.get(x) ?? 0 }))
}
